import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { GrooveActionDirective, GrooveSurfaceDirective } from '@groove/angular';
import {
  JumpNavComponent,
  type JumpNavLink,
} from '../../../../content/jump-nav/jump-nav.component';
import { YoutubeSectionComponent } from '../../../../content/youtube-section/youtube-section.component';
import { GOOGLE_REVIEWS_AGGREGATE } from '../../../../reviews/google-reviews-aggregate.generated';
import { GOOGLE_REVIEWS } from '../../../../reviews/google-reviews.generated';
import { SEOService } from '../../../../seo.service';
import {
  WRITTEN_TESTIMONIALS,
  type WrittenTestimonial,
} from './testimonials-data';
import { buildTestimonialsPageSchemas } from './testimonials-jsonld';

@Component({
  selector: 'app-testimonials-content',
  imports: [
    DatePipe,
    GrooveActionDirective,
    GrooveSurfaceDirective,
    JumpNavComponent,
    YoutubeSectionComponent,
  ],
  templateUrl: './testimonials-content.component.html',
  styleUrl: './testimonials-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestimonialsContentComponent implements OnDestroy {
  private seo = inject(SEOService);

  readonly googleReviews = GOOGLE_REVIEWS;
  readonly googleAggregate = GOOGLE_REVIEWS_AGGREGATE;
  readonly starsRange: readonly number[] = [1, 2, 3, 4, 5];
  readonly writtenTestimonials: readonly WrittenTestimonial[] =
    WRITTEN_TESTIMONIALS;

  protected readonly showAllGoogle = signal(false);
  protected readonly showAllWritten = signal(false);

  protected readonly INITIAL_GOOGLE_COUNT = 8;
  protected readonly INITIAL_WRITTEN_COUNT = 6;

  private readonly gridWritten: readonly WrittenTestimonial[] =
    this.writtenTestimonials;

  protected readonly visibleGoogleReviews = computed(() =>
    this.showAllGoogle()
      ? this.googleReviews
      : this.googleReviews.slice(0, this.INITIAL_GOOGLE_COUNT),
  );

  protected readonly visibleWrittenTestimonials = computed(() =>
    this.showAllWritten()
      ? this.gridWritten
      : this.gridWritten.slice(0, this.INITIAL_WRITTEN_COUNT),
  );

  protected readonly gridWrittenCount = this.gridWritten.length;

  protected toggleAllGoogle(): void {
    this.showAllGoogle.set(true);
  }

  protected toggleAllWritten(): void {
    this.showAllWritten.set(true);
  }

  protected readonly jumpNavLinks: readonly JumpNavLink[] = [
    {
      label: `Google reviews (${this.googleAggregate.count})`,
      fragment: 'google-reviews-heading',
    },
    { label: 'Video testimonials', fragment: 'video-testimonial' },
    { label: 'Written testimonials', fragment: 'written-testimonials' },
  ];

  constructor() {
    // No route param to react to (unlike BlogPostComponent), so a plain
    // constructor call is enough — this page's JSON-LD never changes after
    // mount. Sets the VideoObject + per-review Review schemas that used to
    // live in app.routes.ts's static data.seo.jsonLd; see testimonials-jsonld.ts
    // for why this moved (keeps the ~28kB GOOGLE_REVIEWS array out of the
    // eager route-data bundle).
    this.seo.setRouteSchemas(buildTestimonialsPageSchemas());
  }

  ngOnDestroy(): void {
    this.seo.clearRouteSchemas();
  }
}
